import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { showToast } from '../components/Toast';
import UndoConfirmSheet from '../components/UndoConfirmSheet';
import ConfirmModal from '../components/ConfirmModal';
import StatusBadge from '../components/StatusBadge';
import TaskSubNav from '../components/TaskSubNav';
import TaskAttachments from '../components/TaskAttachments';
import { DSBackLink, DSButton, DSInput, DSIcon, DSBadge } from '../design-system/primitives';
import { userDisplayName } from '../utils/userDisplayName';
import './taskManagementShared.css';
import './TaskDetail.css';

const STATUS_VARIANT = {
  open: 'success',
  in_progress: 'info',
  completed: 'info',
  pending_confirmation: 'warning',
  confirmed: 'success',
  overdue: 'error',
  auto_closed: 'neutral',
  cancelled: 'neutral'
};

function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showUndoSheet, setShowUndoSheet] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const [showCompleteChooser, setShowCompleteChooser] = useState(false);
  const [completeSearch, setCompleteSearch] = useState('');
  const [completeOnBehalfOf, setCompleteOnBehalfOf] = useState(null);

  useEffect(() => {
    setCurrentUser(authService.getCurrentUser());
  }, []);

  const loadTask = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getTask(id);
      setTask(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load task');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  const isDelegate = currentUser?.role === 'admin' || (currentUser?.featureRoles || []).includes('task_management');
  const isAdmin = currentUser?.role === 'admin';
  const assignees = task?.assignees || [];
  const myCompletion = task?.activity?.find((a) => a.action === 'completed' && a.performed_by === currentUser?.id);

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : ' - ');

  const handleComplete = async (onBehalfOfUserId) => {
    try {
      setSubmitting(true);
      await apiService.completeTask(id, onBehalfOfUserId ? { onBehalfOfUserId } : {});
      setShowCompleteChooser(false);
      setCompleteOnBehalfOf(null);
      await loadTask();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to complete task', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const ensureAssignableUsersLoaded = async () => {
    if (assignableUsers.length > 0) {
      return true;
    }
    try {
      const response = await apiService.getTaskAssignableUsers();
      setAssignableUsers(response.data || []);
      return true;
    } catch (err) {
      showToast('Unable to load member list', 'error');
      return false;
    }
  };

  const openCompleteChooser = async () => {
    if (!(await ensureAssignableUsersLoaded())) {
      return;
    }
    setCompleteSearch('');
    setCompleteOnBehalfOf(null);
    setShowCompleteChooser(true);
  };

  const handleClaim = async () => {
    try {
      setSubmitting(true);
      await apiService.claimTask(id);
      await loadTask();
      showToast('Task claimed - it is now in your tasks', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to claim task', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const doRelease = async () => {
    try {
      setSubmitting(true);
      await apiService.releaseTask(id);
      await loadTask();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to release task', 'error');
    } finally {
      setSubmitting(false);
      setConfirmState(null);
    }
  };

  const handleUndoConfirm = async () => {
    try {
      setSubmitting(true);
      await apiService.undoTaskCompletion(id, {});
      setShowUndoSheet(false);
      await loadTask();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to undo completion', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openAssignPanel = async () => {
    if (!(await ensureAssignableUsersLoaded())) {
      return;
    }
    setAssignSearch('');
    setSelectedAssignees(assignees.map((a) => a.userId));
    setShowAssign(true);
  };

  const filteredAssignableUsers = useMemo(() => {
    const query = assignSearch.trim().toLowerCase();
    if (!query) {
      return assignableUsers;
    }
    return assignableUsers.filter((u) => (
      userDisplayName(u).toLowerCase().includes(query)
      || (u.username || '').toLowerCase().includes(query)
    ));
  }, [assignableUsers, assignSearch]);

  const filteredCompleteUsers = useMemo(() => {
    const query = completeSearch.trim().toLowerCase();
    if (!query) {
      return assignableUsers;
    }
    return assignableUsers.filter((u) => (
      userDisplayName(u).toLowerCase().includes(query)
      || (u.username || '').toLowerCase().includes(query)
    ));
  }, [assignableUsers, completeSearch]);

  const handleSaveAssignees = async () => {
    try {
      setSubmitting(true);
      const currentIds = assignees.map((a) => a.userId);
      const toAdd = selectedAssignees.filter((uid) => !currentIds.includes(uid));
      const toRemove = currentIds.filter((uid) => !selectedAssignees.includes(uid));
      if (toAdd.length > 0) {
        await apiService.assignTask(id, toAdd);
      }
      for (const uid of toRemove) {
        await apiService.unassignTask(id, { userId: uid });
      }
      setShowAssign(false);
      await loadTask();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update assignees', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const doCancel = async () => {
    try {
      setSubmitting(true);
      await apiService.cancelTask(id);
      await loadTask();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to cancel task', 'error');
    } finally {
      setSubmitting(false);
      setConfirmState(null);
    }
  };

  const handleReopen = async () => {
    try {
      setSubmitting(true);
      await apiService.reopenTask(id);
      await loadTask();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to reopen task', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    try {
      setSubmitting(true);
      await apiService.dismissTask(id);
      await loadTask();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to dismiss task', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-inline">Loading task...</div>;
  }

  if (error || !task) {
    return <div className="error-message">{error || 'Task not found'}</div>;
  }

  return (
    <div className="tm-page task-detail-page">
      <DSBackLink onClick={() => navigate(-1)}>Back</DSBackLink>

      <div className="tm-section">
        <h1>{task.title}</h1>
        <div className="tm-task-tags">
          <DSBadge variant="brand" size="sm" leadingIcon={<DSIcon name="star" />}>{task.point_value} pts</DSBadge>
          {task.bounty_amount && <StatusBadge variant="warning" size="sm" leadingIcon={<DSIcon name="dollar" />}>{formatCurrency(task.bounty_amount)} bounty</StatusBadge>}
          <StatusBadge label={task.status.replace('_', ' ')} variant={STATUS_VARIANT[task.status] || 'neutral'} uppercase />
        </div>

        <div className="tm-detail-grid">
          <div><strong>Due Date</strong><div>{formatDate(task.due_date)}</div></div>
          <div><strong>Task Type</strong><div>{task.task_type.replace(/_/g, ' ')}</div></div>
          <div>
            <strong>Assignee(s)</strong>
            <div>{assignees.length === 0 ? 'Anyone (open pool)' : assignees.map((a) => a.name).join(', ')}</div>
          </div>
          <div><strong>Category</strong><div>{task.category_name || ' - '}</div></div>
        </div>

        {task.description && (
          <div className="tm-description">
            <strong>Description</strong>
            <p className="tm-preserve-lines">{task.description}</p>
          </div>
        )}

        <TaskAttachments owner="task" ownerId={task.id} />

        {task.current_completion_id && ['completed', 'pending_confirmation', 'confirmed'].includes(task.status) && (
          <TaskAttachments owner="completion" ownerId={task.current_completion_id} title="Completion screenshots" />
        )}

        {task.bounty_amount && (
          <div className="tm-warning-box">
            <DSIcon name="trophy" /> This task has a {formatCurrency(task.bounty_amount)} credit bounty. Credits are paid out after a
            Task Delegate confirms completion.
          </div>
        )}

        {task.canComplete && assignees.length > 0 && (
          <div className="tm-suggestion-meta">
            Marking this complete will award points{task.bounty_amount ? ' and split the bounty' : ''} to: {assignees.map((a) => a.name).join(', ')}
          </div>
        )}

        <div className="tm-actions">
          {task.canComplete && (
            <DSButton
              appearance="solid"
              tone="primary"
              size="md"
              leadingIcon={<DSIcon name="checkCircle" />}
              onClick={() => handleComplete()}
              disabled={submitting}
            >
              Mark Complete{isDelegate ? ' (as me)' : ''}
            </DSButton>
          )}
          {task.canClaim && (
            <DSButton
              appearance="outline"
              tone="secondary"
              size="md"
              leadingIcon={<DSIcon name="user" />}
              onClick={handleClaim}
              disabled={submitting}
            >
              Claim this task
            </DSButton>
          )}
          {task.canComplete && isDelegate && assignees.length === 0 && (
            <DSButton
              appearance="outline"
              tone="secondary"
              size="md"
              leadingIcon={<DSIcon name="users" />}
              onClick={openCompleteChooser}
              disabled={submitting}
            >
              Complete on Behalf of...
            </DSButton>
          )}
          {task.canRelease && (
            <DSButton
              appearance="outline"
              tone="secondary"
              size="md"
              leadingIcon={<DSIcon name="xCircle" />}
              onClick={() => setConfirmState({
                title: 'Release Task',
                message: 'Release yourself from this task?',
                confirmLabel: 'Release',
                onConfirm: doRelease
              })}
              disabled={submitting}
            >
              Release this task
            </DSButton>
          )}
          {myCompletion && ['completed', 'pending_confirmation'].includes(task.status) && (
            <DSButton
              appearance="outline"
              tone="secondary"
              size="md"
              leadingIcon={<DSIcon name="undo" />}
              onClick={() => setShowUndoSheet(true)}
            >
              Undo my completion
            </DSButton>
          )}
        </div>

        {showCompleteChooser && (
          <div className="tm-assign-panel">
            <DSInput
              label="Complete on behalf of"
              type="search"
              placeholder="Search members by name..."
              value={completeSearch}
              onChange={(e) => setCompleteSearch(e.target.value)}
              autoFocus
            />
            <div className="tm-assign-option-list">
              {filteredCompleteUsers.length === 0 ? (
                <div className="tm-suggestion-meta">No members match "{completeSearch}"</div>
              ) : (
                filteredCompleteUsers.map((u) => (
                  <label key={u.id} className="tm-assign-option">
                    <input
                      type="radio"
                      name="completeOnBehalfOf"
                      checked={completeOnBehalfOf === u.id}
                      onChange={() => setCompleteOnBehalfOf(u.id)}
                    />
                    {userDisplayName(u)}
                  </label>
                ))
              )}
            </div>
            <div className="tm-actions">
              <DSButton
                appearance="solid"
                tone="primary"
                size="md"
                leadingIcon={<DSIcon name="checkCircle" />}
                onClick={() => handleComplete(completeOnBehalfOf)}
                disabled={submitting || !completeOnBehalfOf}
              >
                Complete for {completeOnBehalfOf ? (assignableUsers.find((u) => u.id === completeOnBehalfOf)?.first_name || 'selected member') : '...'}
              </DSButton>
              <DSButton appearance="text" tone="neutral" size="md" onClick={() => setShowCompleteChooser(false)} disabled={submitting}>Cancel</DSButton>
            </div>
          </div>
        )}
      </div>

      {isDelegate && (
        <div className="tm-section">
          <h2><DSIcon name="settings" /> Task Delegate Actions</h2>
          <div className="tm-actions">
            <DSButton
              to={`/tasks/${id}/edit`}
              appearance="outline"
              tone="secondary"
              size="md"
              leadingIcon={<DSIcon name="edit" />}
            >
              Edit Task
            </DSButton>
            <DSButton
              appearance="outline"
              tone="secondary"
              size="md"
              leadingIcon={<DSIcon name="users" />}
              onClick={openAssignPanel}
            >
              Assign / Reassign
            </DSButton>
            {['open', 'in_progress', 'overdue'].includes(task.status) && (
              <DSButton
                appearance="outline"
                tone="danger"
                size="md"
                leadingIcon={<DSIcon name="block" />}
                onClick={() => setConfirmState({
                  title: 'Cancel Task',
                  message: 'Cancel this task? It can be reopened later if needed.',
                  confirmLabel: 'Cancel Task',
                  danger: true,
                  onConfirm: doCancel
                })}
                disabled={submitting}
              >
                Cancel Task
              </DSButton>
            )}
            {['cancelled', 'auto_closed'].includes(task.status) && (
              <DSButton
                appearance="outline"
                tone="secondary"
                size="md"
                leadingIcon={<DSIcon name="refresh" />}
                onClick={handleReopen}
                disabled={submitting}
              >
                Reopen
              </DSButton>
            )}
            {task.status === 'auto_closed' && (
              <DSButton
                appearance="outline"
                tone="secondary"
                size="md"
                leadingIcon={<DSIcon name="xCircle" />}
                onClick={handleDismiss}
                disabled={submitting}
              >
                Dismiss
              </DSButton>
            )}
            {task.status === 'pending_confirmation' && (
              <DSButton
                to={`/tasks/${id}/confirm-bounty`}
                appearance="solid"
                tone="primary"
                size="md"
                leadingIcon={<DSIcon name="dollar" />}
              >
                Confirm Bounty
              </DSButton>
            )}
            {task.status === 'confirmed' && isAdmin && (
              <DSButton
                to={`/tasks/${id}/force-undo`}
                appearance="solid"
                tone="danger"
                size="md"
                leadingIcon={<DSIcon name="undo" />}
              >
                Force Undo
              </DSButton>
            )}
            {['completed', 'pending_confirmation'].includes(task.status) && !myCompletion && (
              <DSButton
                to={`/tasks/${id}/force-undo`}
                appearance="outline"
                tone="danger"
                size="md"
                leadingIcon={<DSIcon name="undo" />}
              >
                Undo Completion
              </DSButton>
            )}
          </div>

          {showAssign && (
            <div className="tm-assign-panel">
              <DSInput
                type="search"
                placeholder="Search members by name..."
                value={assignSearch}
                onChange={(e) => setAssignSearch(e.target.value)}
                autoFocus
              />
              <div className="tm-assign-option-list">
                {filteredAssignableUsers.length === 0 ? (
                  <div className="tm-suggestion-meta">No members match "{assignSearch}"</div>
                ) : (
                  filteredAssignableUsers.map((u) => (
                    <label key={u.id} className="tm-assign-option">
                      <input
                        type="checkbox"
                        checked={selectedAssignees.includes(u.id)}
                        onChange={(e) => {
                          setSelectedAssignees((prev) => (
                            e.target.checked ? [...prev, u.id] : prev.filter((uid) => uid !== u.id)
                          ));
                        }}
                      />
                      {userDisplayName(u)}
                    </label>
                  ))
                )}
              </div>
              {selectedAssignees.length > 0 && (
                <div className="tm-suggestion-meta">{selectedAssignees.length} selected</div>
              )}
              <div className="tm-actions">
                <DSButton appearance="solid" tone="primary" size="md" leadingIcon={<DSIcon name="check" />} onClick={handleSaveAssignees} disabled={submitting}>Save</DSButton>
                <DSButton appearance="text" tone="neutral" size="md" onClick={() => setShowAssign(false)}>Cancel</DSButton>
              </div>
            </div>
          )}

          {task.activity && task.activity.length > 0 && (
            <div className="tm-activity-log">
              <h3>Activity</h3>
              <ul>
                {task.activity.map((entry) => (
                  <li key={entry.id}>
                    <strong>{entry.action.replace(/_/g, ' ')}</strong> by {entry.performed_by_name || 'system'} on {new Date(entry.created_at).toLocaleString()}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {showUndoSheet && (
        <UndoConfirmSheet
          pointValue={task.point_value}
          submitting={submitting}
          onConfirm={handleUndoConfirm}
          onCancel={() => setShowUndoSheet(false)}
        />
      )}
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={submitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
      <TaskSubNav />
    </div>
  );
}

export default TaskDetail;
